/**
 * Plain-language model for the simple app builder. Every edit takes an app/v2 definition
 * and returns a new one, writing the same fields the advanced Layout tab uses.
 */
import { buildAppPublishReadiness } from '../../../utils/studioV2AppDiagnostics.js';
import { cleanGridCells, gridCellsFor } from '../../../utils/gridCells.js';

export const SIMPLE_LAYOUTS = [
  {
    id: 'three-panel',
    label: 'Chat in the middle',
    description: 'One panel on each side of the conversation',
    zones: ['left', 'center', 'right'],
    sidesHoldOne: true,
  },
  {
    id: 'dynamic',
    label: 'Stacked panels',
    description: 'Several panels stacked beside the conversation',
    zones: ['left', 'center', 'right'],
    sidesHoldOne: false,
  },
  {
    id: 'two-panel',
    label: 'Two columns',
    description: 'Two panels side by side, no center chat',
    zones: ['left', 'right'],
    sidesHoldOne: true,
  },
];

export const ZONE_LABELS = {
  left: 'Left', center: 'Center', right: 'Right', top: 'Top', bottom: 'Bottom',
};

export const PANEL_KINDS = [
  {
    kind: 'chat', type: 'chat', label: 'Chat assistant', description: 'A conversation with your AI flow',
  },
  {
    kind: 'summary', type: 'summary', label: 'Conversation summary', description: 'A running recap of the chat',
  },
  {
    kind: 'table', type: 'component', label: 'Table', description: 'Rows of results, like candidates or cases',
  },
  {
    kind: 'stats', type: 'component', label: 'Key numbers', description: 'A few headline figures',
  },
  {
    kind: 'chart', type: 'component', label: 'Chart', description: 'Trends and comparisons',
  },
  {
    kind: 'rich_text', type: 'component', label: 'Report', description: 'Formatted text written by the flow',
  },
  {
    kind: 'form', type: 'component', label: 'Form', description: 'Collect details from the person',
  },
  {
    kind: 'list', type: 'component', label: 'List', description: 'A simple list of items',
  },
  {
    kind: 'timeline', type: 'component', label: 'Timeline', description: 'Steps or events in order',
  },
  {
    kind: 'grid', type: 'component', label: 'Cards', description: 'A grid of cards',
  },
  {
    kind: 'map', type: 'component', label: 'Map', description: 'Places on a map',
  },
  {
    kind: 'image', type: 'component', label: 'Image', description: 'A picture from the flow',
  },
  {
    kind: 'skill_chat', type: 'component', label: 'Skill chat', description: 'Talk directly to one of your skills',
  },
  {
    kind: 'plugin', type: 'component', label: 'Plugin panel', description: 'A panel drawn by an installed plugin', pickerHidden: true,
  },
];

const KIND_BY_ID = Object.fromEntries(PANEL_KINDS.map((k) => [k.kind, k]));

const DEFAULT_OUTPUT_KEY = {
  table: 'table_data',
  chart: 'chart_data',
  form: 'form_prefill',
  map: 'map_markers',
  rich_text: 'rich_text',
  image: 'image_url',
  grid: 'grid_data',
  stats: 'stats_data',
  list: 'list_data',
  timeline: 'timeline_data',
  skill_chat: 'skill_chat',
  plugin: 'plugin_data',
};

export function parseAppText(text) {
  try {
    const d = JSON.parse(text || '{}');
    if (!d || typeof d !== 'object' || Array.isArray(d)) return null;
    return { ...d, panels: Array.isArray(d.panels) ? d.panels : [] };
  } catch {
    return null;
  }
}

export const serializeApp = (def) => JSON.stringify(def, null, 2);

export function panelKind(panel) {
  if (!panel) return null;
  if (panel.type === 'chat' || panel.type === 'summary') return panel.type;
  return panel.componentType || 'table';
}

export function panelLabel(panel) {
  const kind = panelKind(panel);
  if (kind === 'plugin' && panel?.config?.title) return panel.config.title;
  return KIND_BY_ID[kind]?.label || String(kind || 'Panel');
}

export function layoutInfo(layoutId) {
  return SIMPLE_LAYOUTS.find((l) => l.id === layoutId) || null;
}

const sortByOrder = (panels) => [...panels].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

export function panelsInZone(def, zone) {
  return sortByOrder((def?.panels || []).filter((p) => p.position === zone));
}

export const isGridApp = (def) => def?.layout === 'grid';

/** Whether a new panel fits in `zone`, and whether the layout must switch to stacked panels. */
export function zoneCapacity(def, zone) {
  if (isGridApp(def)) return { fits: true };
  const layout = layoutInfo(def?.layout) || SIMPLE_LAYOUTS[0];
  if (!layout.zones.includes(zone)) return { fits: false, reason: 'Not part of this layout' };
  const used = panelsInZone(def, zone).length;
  if (zone === 'center') return used ? { fits: false, reason: 'The center holds one panel' } : { fits: true };
  if (!layout.sidesHoldOne || used === 0) return { fits: true };
  if (layout.id === 'three-panel') return { fits: true, switchTo: 'dynamic' };
  return { fits: false, reason: 'Each column holds one panel' };
}

function uniquePanelId(def, kind) {
  const ids = new Set((def.panels || []).map((p) => p.id));
  let n = 1;
  while (ids.has(`${kind}_${n}`)) n += 1;
  return `${kind}_${n}`;
}

/** `plugin` (from /extensions/app-panels) is required when kind is 'plugin'. */
export function addPanel(def, kind, zone, plugin = null) {
  const info = KIND_BY_ID[kind];
  if (!info || (kind === 'plugin' && !plugin?.ref)) return def;
  const capacity = zoneCapacity(def, zone);
  if (!capacity.fits) return def;
  const panel = {
    id: uniquePanelId(def, kind),
    type: info.type,
    position: zone,
    order: panelsInZone(def, zone).length,
  };
  if (info.type === 'chat') panel.fixed = true;
  if (info.type === 'component') {
    panel.componentType = kind;
    panel.outputKey = DEFAULT_OUTPUT_KEY[kind] || `${kind}_data`;
    if (kind === 'form') {
      panel.config = {
        formFields: [{
          id: 'notes', type: 'textarea', label: 'Notes', required: false,
        }],
      };
    }
    if (kind === 'plugin') {
      panel.outputKey = plugin.outputKey || panel.outputKey;
      panel.config = { pluginPanel: plugin.ref, title: plugin.name };
    }
  }
  const panels = [...(def.panels || []), panel];
  return {
    ...def,
    ...(capacity.switchTo ? { layout: capacity.switchTo } : {}),
    panels,
    ...(isGridApp(def) ? { gridLayout: gridCellsFor(panels, def.gridLayout) } : {}),
  };
}

export function removePanel(def, panelId) {
  const next = { ...def, panels: (def.panels || []).filter((p) => p.id !== panelId) };
  if (Array.isArray(def.gridLayout)) next.gridLayout = def.gridLayout.filter((c) => String(c.i) !== String(panelId));
  return next;
}

/** Cells to draw in the grid arranger: saved ones plus defaults for panels without a cell. */
export const gridCellsOf = (def) => gridCellsFor(def?.panels, def?.gridLayout);

export function setGridLayout(def, layout) {
  return { ...def, gridLayout: cleanGridCells(layout) };
}

export function movePanel(def, panelId, direction) {
  const self = (def.panels || []).find((p) => p.id === panelId);
  if (!self) return def;
  const zonePanels = panelsInZone(def, self.position);
  const idx = zonePanels.findIndex((p) => p.id === panelId);
  const swap = direction === 'up' ? idx - 1 : idx + 1;
  if (swap < 0 || swap >= zonePanels.length) return def;
  [zonePanels[idx], zonePanels[swap]] = [zonePanels[swap], zonePanels[idx]];
  const reordered = zonePanels.map((p, i) => ({ ...p, order: i }));
  return { ...def, panels: [...def.panels.filter((p) => p.position !== self.position), ...reordered] };
}

export function setLayout(def, layoutId) {
  return { ...def, layout: layoutId };
}

export const panelPhase = (panel) => (panel?.config?.interviewPhase ? String(panel.config.interviewPhase) : '');

/**
 * Panels the chosen layout will not show (e.g. the center chat in two columns). The runtime
 * filters by interview phase first, so one-per-side layouts keep one panel per side per phase.
 */
export function hiddenPanelsForLayout(def, layoutId) {
  const layout = layoutInfo(layoutId);
  if (!layout) return [];
  const hidden = [];
  for (const zone of ['left', 'center', 'right', 'top', 'bottom']) {
    const panels = panelsInZone(def, zone);
    if (!layout.zones.includes(zone)) hidden.push(...panels);
    else if (zone !== 'center' && layout.sidesHoldOne) {
      const seenPhases = new Set();
      panels.forEach((p) => {
        const phase = panelPhase(p);
        if (seenPhases.has(phase)) hidden.push(p);
        seenPhases.add(phase);
      });
    }
  }
  return hidden;
}

function updatePanel(def, panelId, fn) {
  return { ...def, panels: (def.panels || []).map((p) => (p.id === panelId ? fn(p) : p)) };
}

function patchSource(panel, partial) {
  const source = { ...(panel.source || {}) };
  for (const [k, v] of Object.entries(partial)) {
    const val = typeof v === 'string' ? v.trim() : v;
    if (val === '' || val == null) delete source[k];
    else source[k] = val;
  }
  const next = { ...panel };
  if (Object.keys(source).length) next.source = source;
  else delete next.source;
  return next;
}

export const setPanelFlow = (def, panelId, flowRef) => updatePanel(def, panelId, (p) => patchSource(p, { flowRef }));

export const setPanelResultField = (def, panelId, outputPath) => updatePanel(def, panelId, (p) => patchSource(p, { outputPath }));

export function setPanelConfig(def, panelId, partial) {
  return updatePanel(def, panelId, (p) => {
    const config = { ...(p.config || {}), ...partial };
    Object.keys(config).forEach((k) => {
      if (config[k] === '' || config[k] == null) delete config[k];
    });
    return { ...p, config };
  });
}

export const setPanelSkill = (def, panelId, skill) => setPanelConfig(def, panelId, {
  skillId: skill?.id || null,
  skillName: skill?.name || null,
});

export const bindingCount = (panel) => (Array.isArray(panel?.source?.bindings) ? panel.source.bindings.length : 0);

function interviewFlowOf(def) {
  const m = def?.metadata;
  return m && typeof m === 'object' && m.interviewFlow && typeof m.interviewFlow === 'object' ? m.interviewFlow : {};
}

function patchInterviewFlow(def, partial) {
  const merged = { ...interviewFlowOf(def), ...partial };
  Object.keys(merged).forEach((k) => {
    if (merged[k] === '' || merged[k] == null) delete merged[k];
  });
  return { ...def, metadata: { ...(def.metadata || {}), interviewFlow: merged } };
}

export const chatFlowOf = (def) => def?.defaultFlowId || interviewFlowOf(def).defaultChatFlowRef || '';

export function setChatFlow(def, flowId) {
  const v = String(flowId || '').trim();
  const next = patchInterviewFlow(def, { defaultChatFlowRef: v || undefined });
  if (v) next.defaultFlowId = v;
  else delete next.defaultFlowId;
  return next;
}

export function chatMemoryOf(def) {
  const itf = interviewFlowOf(def);
  return {
    rememberOnDevice: Boolean(itf.persistSharedChatHistory) && itf.restoreSharedChatHistoryOnLoad !== false,
    shareWithPanels: Boolean(itf.sharedChatHistoryAcrossPanels),
  };
}

/** Choosing these in the simple UI counts as having reviewed the shared-chat settings. */
export function setChatMemory(def, { rememberOnDevice, shareWithPanels }) {
  const partial = { sharedChatSettingsReviewed: true };
  if (rememberOnDevice !== undefined) {
    partial.persistSharedChatHistory = rememberOnDevice;
    partial.restoreSharedChatHistoryOnLoad = rememberOnDevice;
  }
  if (shareWithPanels !== undefined) partial.sharedChatHistoryAcrossPanels = shareWithPanels;
  return patchInterviewFlow(def, partial);
}

export function setTheme(def, partial) {
  const theme = { ...(def.theme && typeof def.theme === 'object' ? def.theme : {}), ...partial };
  Object.keys(theme).forEach((k) => {
    if (theme[k] === '' || theme[k] == null) delete theme[k];
  });
  return { ...def, theme };
}

export function setDetails(def, partial) {
  const next = { ...def, ...partial };
  if (next.description === '') delete next.description;
  return next;
}

const READINESS_COPY = {
  schema: { label: 'App settings are complete', fix: 'advanced-json', fixLabel: 'Show details' },
  flows: { label: 'Every data panel is connected to a flow', fix: 'design', fixLabel: 'Connect panels' },
  bindings: { label: 'Panels match what their flows return', fix: 'advanced-layout', fixLabel: 'Review fields' },
  preview: {
    label: 'Preview has sample data', fix: 'advanced-layout', fixLabel: 'Add sample data', optional: true,
  },
  'public-route': { label: 'Public sharing settings checked', fix: 'review', fixLabel: 'Mark as checked' },
  'shared-chat': { label: 'Shared chat settings checked', fix: 'review', fixLabel: 'Mark as checked' },
};

/** Publish readiness in plain language; optional items do not block "ready". */
export function friendlyReadiness(def) {
  if (!def) {
    return {
      ready: false,
      done: 0,
      total: 1,
      items: [{
        id: 'json', label: 'App settings can be read', ok: false, fix: 'advanced-json', fixLabel: 'Show details',
      }],
    };
  }
  const { items, errors } = buildAppPublishReadiness(def);
  const mapped = items.map((item) => ({
    ...item,
    ...(READINESS_COPY[item.id] || { label: item.label }),
    ...(!item.ok && (item.id === 'bindings' || item.id === 'schema') && errors?.[0] ? { detail: errors[0] } : {}),
  }));
  const required = mapped.filter((i) => !i.optional);
  const done = required.filter((i) => i.ok).length;
  return {
    ready: done === required.length, done, total: required.length, items: mapped,
  };
}

export function markReviewed(def, itemId) {
  if (itemId === 'public-route') return patchInterviewFlow(def, { publicRouteReviewed: true });
  if (itemId === 'shared-chat') return patchInterviewFlow(def, { sharedChatSettingsReviewed: true });
  return def;
}

/** Column boxes for a mini layout thumbnail: [{ zone, kinds: [...] }]. */
export function layoutThumbnail(def) {
  const layout = layoutInfo(def?.layout) || SIMPLE_LAYOUTS[0];
  return layout.zones.map((zone) => ({
    zone,
    kinds: panelsInZone(def, zone)
      .slice(0, zone === 'center' || layout.sidesHoldOne ? 1 : 3)
      .map((p) => panelKind(p)),
  }));
}
